(ns graph-ui.web.reopen
  "The Reopen dialog (IKC3/PLAN-ikc-v3-080-01/REPORT, screen 1b of the
   plan, dsl/contexts/workflow.edn :reopen).

   DECISION 6 (George, 2026-10-13): \"The agent usually still keeps the
   worktree and can continue on short notice\". So Reopen sends a deployed
   work item back to Started, with the same assignee. The person writes
   what is still wrong, removes the tick of each criterion that failed,
   and can add files. No picture is taken, because this dialog is on the
   board.

   ONE COMMAND. The dialog sends :workflow/reopen, and the bus does the
   move, the criteria and the comment, each by its own command and rule.
   A refusal is the sentence of the status line, and the words stay.

   The words of the dialog are support.edn :forms :reopen, read with
   :support/report-form. The item is read with :workflow/work-item.

   Every child list is spread with `into` (CLAUDE.md, the Reagent gotcha)."
  (:require [clojure.string :as str]
            [reagent.core :as r]
            [graph-ui.web.support :as support]))

;; @plan PLAN-ikc-v3-080-01-the-tracker-v2

(def ^:private empty-state
  {:open? false :bus nil :item-id nil :item nil :answer nil
   :reason "" :failed #{} :files [] :file-message nil :to :started
   :phase :writing :message nil})

(defonce ^:private state (r/atom empty-state))

(defn- answer-of [reply] (if (sequential? reply) (first reply) reply))

(defn dialog-state
  "The state of the dialog. For the tests and for a probe."
  []
  @state)

(defn refusal-of
  "PURE: the sentence of a refused reply of :workflow/reopen, or nil when
   the item was reopened."
  [reply]
  (let [evs (if (sequential? reply) reply [reply])
        ok  (first (filter #(= :workflow/reopened (:type %)) evs))
        e   (first evs)]
    (cond
      ok                             nil
      (nil? e)                       "The bus gave no answer."
      (:rejected e)                  (str "The bus refused the reopen: "
                                          (some-> (get-in e [:rejected :reason]) name) ".")
      (:message e)                   (:message e)
      :else                          (str "The bus answered: " (pr-str e)))))

(defn done-message
  "PURE: the sentence after a reopen, from the event :workflow/reopened."
  [reply]
  (let [ev (first (filter #(= :workflow/reopened (:type %)) (if (sequential? reply) reply [reply])))]
    (str "The work item is in Started again, with the same assignee."
         (when (= :speed-lane (:to ev)) " Its priority is Highest.")
         (when (seq (:opened ev)) (str " " (count (:opened ev)) " criterion is open again."))
         (when (seq (:not-opened ev))
           (str " Not opened: " (str/join " " (map :message (:not-opened ev))))))))

(defn failed-ids
  "PURE: the ids of the proven criteria of `item` whose tick the person
   removed (`unticked`)."
  [item unticked]
  (vec (for [c (:criteria item)
             :when (= :proven (keyword (:state c)))
             :when (contains? unticked (:id c))]
         (:id c))))

(defn set-reason!
  "The person wrote the reason."
  [text]
  (swap! state assoc :reason (str text) :phase :writing))

(defn toggle-failed!
  "The person removed or gave back the tick of the criterion `cid`."
  [cid]
  (swap! state update :failed (fn [f] (if (contains? f cid) (disj f cid) (conj f cid)))))

(defn open!
  "Open the dialog for the work item `item-id`."
  [{:keys [bus item-id]}]
  (reset! state (assoc empty-state :open? true :bus bus :item-id item-id))
  (when bus
    (js-invoke bus "dispatch" {:type :workflow/work-item :item-id item-id}
               (fn [reply] (swap! state assoc :item (:item (answer-of reply)))))
    (js-invoke bus "dispatch" {:type :support/report-form}
               (fn [reply] (swap! state assoc :answer (answer-of reply))))))

(defn close!
  "Close the dialog. The words stay until the next open of another item."
  []
  (swap! state assoc :open? false))

(defn submit!
  "Send :workflow/reopen."
  []
  (let [{:keys [bus item-id item reason failed files to phase]} @state]
    (when (and bus (not (str/blank? reason)) (not= :sending phase))
      (swap! state assoc :phase :sending :message nil)
      (js-invoke bus "dispatch"
                 (cond-> {:type :workflow/reopen :item-id item-id :reason reason :to to}
                   (seq (failed-ids item failed)) (assoc :failed (failed-ids item failed))
                   (seq files)                    (assoc :files (mapv :path files)))
                 (fn [reply]
                   (if-let [why (refusal-of reply)]
                     (swap! state assoc :phase :refused :message why)
                     (swap! state assoc :phase :done :message (done-message reply))))))))

(def ^:private label-style {:fontWeight 600 :fontSize "13px" :margin "10px 0 4px"})
(def ^:private hint-style {:fontSize "12px" :color "var(--muted)"})
(def ^:private button-style
  {:padding "5px 10px" :fontSize "13px" :background "transparent" :color "var(--text)"
   :border "1px solid var(--border)" :borderRadius "4px" :cursor "pointer"})

(defn reopen-dialog
  "The Reopen dialog, drawn at the root beside the report dialog."
  []
  (let [{:keys [open? item answer reason failed files file-message to phase message bus]} @state
        w     (merge {:title "Reopen" :reason-label "What is still wrong?" :send "Reopen"}
                     (get-in answer [:forms :reopen]))
        pw    (get-in answer [:forms :picture])
        busy? (= :sending phase)]
    (when open?
      [:div {:data-import-id "reopen-dialog" :role "dialog" :aria-modal "true"
             :aria-label (:title w)
             :on-key-down (fn [e] (when (= "Escape" (.-key e)) (.preventDefault e) (close!)))
             :style {:position "fixed" :inset 0 :zIndex 3000 :display "flex"
                     :alignItems "center" :justifyContent "center"
                     :background "rgba(0,0,0,0.35)"}}
       [:div {:style {:background "var(--panel)" :color "var(--text)"
                      :border "1px solid var(--border)" :borderRadius "8px" :padding "16px"
                      :width "min(640px, calc(100vw - 32px))" :boxSizing "border-box"
                      :maxHeight "calc(100vh - 32px)" :overflowY "auto"
                      :boxShadow "0 8px 32px rgba(0,0,0,0.3)"}}
        [:div {:style {:fontWeight 600 :fontSize "16px" :marginBottom "6px"}}
         [:span {:style {:fontFamily "monospace" :fontSize "13px" :marginRight "8px"}} (:key item)]
         (str (:title w) " — " (:title item))]
        [:div {:style label-style} (:reason-label w)]
        [:textarea {:data-import-id "reopen-dialog/reason" :value reason :rows 3 :auto-focus true
                    :read-only busy?
                    :on-change #(set-reason! (.. % -target -value))
                    :style {:width "100%" :boxSizing "border-box" :padding "8px" :fontSize "14px"
                            :fontFamily "inherit" :background "var(--card)" :color "var(--text)"
                            :border "1px solid var(--border)" :borderRadius "4px"}}]
        (when (seq (:criteria item))
          [:div {:data-import-id "reopen-dialog/criteria"}
           [:div {:style label-style} (:criteria-label w)]
           [:div {:style hint-style} (:criteria-hint w)]
           (into [:div {:style {:display "flex" :flexDirection "column" :gap "2px" :marginTop "4px"}}]
                 (for [{:keys [id text state verifier-label]} (:criteria item)
                       :let [proven? (= :proven (keyword state))]]
                   ^{:key id}
                   [:label {:style {:fontSize "13px" :display "flex" :gap "6px" :alignItems "center"}}
                    [:input {:type "checkbox" :disabled (or busy? (not proven?))
                             :checked (and proven? (not (contains? failed id)))
                             :on-change #(toggle-failed! id)}]
                    text
                    [:span {:style hint-style} (str "· " verifier-label (when-not proven? " · open"))]]))])
        [:div {:style label-style} (:files-label w)]
        [:div {:style {:display "flex" :gap "8px" :alignItems "center" :flexWrap "wrap"}}
         [:label {:data-import-id "reopen-dialog/add-file" :style (merge button-style {:display "inline-block"})}
          (or (:add-file pw) "Add a file")
          [:input {:type "file" :multiple true :disabled busy? :style {:display "none"}
                   :on-change (fn [e]
                                (when bus (support/attach-files! state bus (.. e -target -files)))
                                (set! (.. e -target -value) ""))}]]
         [:span {:style hint-style} (:files-hint w)]]
        (when (seq files)
          (into [:div {:style {:fontSize "13px" :marginTop "4px"}}]
                (for [{:keys [name path]} files] ^{:key path} [:span {:style {:marginRight "10px"}} name])))
        (when file-message [:div {:style {:fontSize "12px" :color "var(--crit)"}} file-message])
        [:div {:style label-style} (:to-label w)]
        (into [:div {:style {:display "flex" :gap "6px" :flexWrap "wrap"}}]
              (for [{:keys [id label hint]} (:to w)]
                ^{:key (str id)}
                [:button {:data-import-id (str "reopen-dialog/to-" (name id))
                          :aria-pressed (= id to)
                          :on-click #(swap! state assoc :to id)
                          :style {:textAlign "left" :padding "6px 10px" :borderRadius "6px" :cursor "pointer"
                                  :background (if (= id to) "var(--hover)" "transparent") :color "var(--text)"
                                  :border (if (= id to) "2px solid var(--accent)" "1px solid var(--border)")}}
                 [:div {:style {:fontWeight 600 :fontSize "13px"}} label]
                 [:div {:style hint-style} hint]]))
        [:div {:data-import-id "reopen-dialog/status" :role "status"
               :style {:fontSize "13px" :minHeight "1.4em" :margin "10px 0 8px"
                       :color (if (= :refused phase) "var(--crit)" "var(--muted)")}}
         (or message " ")]
        [:div {:style {:display "flex" :gap "8px" :justifyContent "flex-end" :alignItems "center"}}
         [:span {:style (merge hint-style {:marginRight "auto"})} (:footer w)]
         [:button {:data-import-id "reopen-dialog/close" :on-click close! :style button-style}
          (if (= :done phase) "Close" "Cancel")]
         (when-not (= :done phase)
           [:button {:data-import-id "reopen-dialog/send"
                     :disabled (or busy? (str/blank? reason))
                     :on-click submit!
                     :style {:padding "6px 12px" :fontSize "13px" :background "var(--accent)"
                             :color "white" :border "none" :borderRadius "4px" :cursor "pointer"
                             :opacity (if (or busy? (str/blank? reason)) 0.5 1)}}
            (:send w)])]]])))
